/**
 *  @author: Maximilian Gertz
 *  @email: mgertz@nanocosmos.net
 *  @organization: Nanocosmos GmbH
 *  @version: (introduced in analytics-front) 2.26.0
 */

// React Imports
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { Line } from 'react-chartjs-2';
import { Tooltip as ReactTooltip } from 'react-tooltip/dist/react-tooltip.umd';
import { CardTitle, Col, Row } from 'reactstrap';
import ReactDOMServer from 'react-dom/server';

// Redux Imports
import { useSelector, useDispatch } from 'react-redux';
import {
   alertFineStartSelector,
   alertFineEndSelector,
   troubleIntervalSelector,
   hashSelector,
   cancelTokenSelector,
   troubleModeSelector,
} from '../../../../../../redux/selectors/selectors';
import { setTroubleshootingAlertFineTimespan } from '../../../../../../redux/actions/general';
import { URL_PARAMS } from '../../../../../../urlSync/urlParamNames';

// External Libraries
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getStyle } from '@coreui/coreui-pro/dist/js/coreui-utilities';
import { colorToRgba } from '../../../../../../util/UtilFunctions';
import moment from 'moment';
import axios from 'axios';

// Custom Imports
import { ALERT_MAPPER } from './AlertDetailsMapper';
import {
   ChartWrapper,
   SetChartHeight,
   AlignedIconContainer,
   HeightSetContainer,
   PaddingContainer,
   SimpleFlexItem,
   SimpleFlexRow,
   TwoSideFlexContainer,
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
   CenteredMsgShowing,
   BottomMargin,
   StandardContainer,
   Description,
   ValueText,
   CenterContentContainer,
   FlexRow,
   FontWeightedText,
   HintInfo,
} from '../../../../../../styledComponents/styledComponents';
import {
   aggEventWithSuggestedMinChartOptions,
   getOptionsWithAnnotationsEvents,
} from '../../../../../../constants/chartjs';
import {
   CHRONOLOGICAL_DATE_TIME_FORMAT,
   CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED,
   SEARCH_TYPE,
   MAX_DURATION_IN_MS,
} from '../../../constants';
import { Spinner, CsvExportFromApiButton } from '../../../../../common';
import { getHttpHeader } from '../../../../../../util/FetchTools';
import AlertDetailsPanel from './AlertDetailsPanel';
import { DetailTile, TileContainer } from '../../../Styles';
import TextWithToolTip from '../../../TextWithToolTip';
import { E2E } from '../../../../../../constants/general';
import DateTimeSlider from '../../../general/DateTimeSlider';
import { getIntervalStart, getIntervalEnd } from '../../../general/DataProcessing';
import { ALERT_FINE_START_REDUCTION_IN_MINUTES, ALERT_FINE_END_EXTENSION_IN_MINUTES } from '../constants';

const STR_DETAILS = [
   {
      name: 'Suboptimal STR Occurences',
      description:
         'Count of stream time ratio stats that were <= 0.93 (lower orange line) and > 0.86 (lower red line) or >= 1.07 (upper orange line) and < 1.14 (upper red line) within the displayed time range.',
   },
   {
      name: 'Poor STR Occurences',
      description:
         'Count of stream time ratio stats that were <= 0.86 (lower red line) or >= 1.14 (upper red line) within the displayed time range.',
   },
];

const TOLERANCE_LEVEL = {
   WARNING: 'warning',
   ERROR: 'error',
};

const STREAM_TIME_RATIO_TOLERANCE = {
   [TOLERANCE_LEVEL.WARNING]: 0.07,
   [TOLERANCE_LEVEL.ERROR]: 0.14,
};

function isStrValueBeyondTolerance(str, toleranceLevel) {
   const tolerance = STREAM_TIME_RATIO_TOLERANCE[toleranceLevel];
   return str >= 1 + tolerance || str <= 1 - tolerance;
}

function getPerformanceAnnotations(selectedAlert, isStreamFinishedAfterAlert) {
   const startAndEndIsSame = selectedAlert.first === selectedAlert.last;

   const annotations = [
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 1,
         borderColor: '#34d746',
         borderWidth: 2,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 0.93,
         borderColor: '#ed7d0e',
         borderWidth: 2,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 1.07,
         borderColor: '#ed7d0e',
         borderWidth: 2,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 0.86,
         borderColor: '#e92b2b',
         borderWidth: 2,
      },
      {
         type: 'line',
         mode: 'horizontal',
         scaleID: 'y-axis-0',
         value: 1.14,
         borderColor: '#e92b2b',
         borderWidth: 2,
      },
      {
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value: moment.utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT).subtract(5, 'minutes').format(),
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: 'Detection Start',
            enabled: true,
            position: 'bottom',
            yAdjust: 75,
         },
      },
      {
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value: moment.utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT).format(),
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: startAndEndIsSame ? 'Emitted and stopped Alert' : 'Emitted Alert',
            enabled: true,
            position: 'bottom',
            yAdjust: 51,
         },
      },
      {
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value:
            selectedAlert.status === 'Terminated'
               ? moment.utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT).format()
               : undefined,
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: isStreamFinishedAfterAlert ? 'Stream stopped' : 'Stream recovered',
            enabled: true,
            position: 'bottom',
            yAdjust: 3,
         },
      },
   ];

   if (!startAndEndIsSame) {
      annotations.push({
         type: 'line',
         mode: 'vertical',
         scaleID: 'x-axis-0',
         value:
            selectedAlert.status === 'Terminated'
               ? moment.utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT).format()
               : undefined,
         borderColor: 'black',
         borderWidth: 1.5,
         label: {
            backgroundColor: 'rgba(0,0,0, 0.5)',
            content: 'Alert stopped',
            enabled: true,
            position: 'bottom',
            yAdjust: 27,
         },
      });
   }

   return annotations;
}

function StrPerformanceWidget({ streamName, selectedAlert, height }) {
   const ALERT_CODE = selectedAlert.codes[0];
   const { title, alertInfo, metricPath, maxRtmpStatDisplayRangeInMS, isExtendedDatapointRequired } = ALERT_MAPPER[
      ALERT_CODE
   ];

   // --------------------------
   // ## React useDispatch hook (REDUX)
   // --------------------------

   const dispatch = useDispatch();

   // --------------------------
   // ## React useSelector hooks
   // --------------------------

   const hash = useSelector(hashSelector);
   const cancelToken = useSelector(cancelTokenSelector);

   const alertFineStart = useSelector(alertFineStartSelector);
   const alertFineStartMoment = moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT);

   const alertFineEnd = useSelector(alertFineEndSelector);
   const alertFineEndMoment = moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT);

   const interval = useSelector(troubleIntervalSelector);
   const mode = useSelector(troubleModeSelector);

   // --------------------------
   // ## React useState hooks
   // --------------------------

   const [chartData, setChartData] = useState({ labels: [], datasets: [] });
   const [chartOptions, setChartOptions] = useState({});
   const [occurences, setOccurences] = useState(
      STR_DETAILS.map((strType) => {
         return { name: strType.name, count: 0 };
      }),
   );

   // --------------------------
   // ## React useMemo hooks
   // --------------------------

   const header = useMemo(() => getHttpHeader(hash), [hash]);

   // --------------------------
   // ## React useCallback hooks
   // --------------------------

   const setAlertFineInterval = useCallback(
      (fineInterval) => {
         dispatch(
            setTroubleshootingAlertFineTimespan(
               { alertFineStart: fineInterval.fineStart, alertFineEnd: fineInterval.fineEnd },
               {
                  [URL_PARAMS.ALERT_FINE_START]: fineInterval.fineStart,
                  [URL_PARAMS.ALERT_FINE_END]: fineInterval.fineEnd,
               },
            ),
         );
      },
      [dispatch],
   );

   // --------------------------
   // ## React useQuery hooks
   // --------------------------

   const diff = alertFineEndMoment.diff(alertFineStartMoment, 'milliseconds');
   const alertDurationCanBeDisplayed = diff < MAX_DURATION_IN_MS;
   const isQueryEnabled =
      metricPath !== undefined && alertFineStart !== '' && alertFineEnd !== '' && alertDurationCanBeDisplayed;

   const { isLoading, isFetching, data: rtmpStats } = useQuery({
      enabled: isQueryEnabled,
      queryKey: [alertFineStart, alertFineEnd],
      queryFn: async () => {
         // prettier-ignore
         const url = `/v2/troubleshooting/rtmp/${metricPath}/raw/timeseries?streams=${streamName}&from=${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}&to=${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}&interval=${interval.value.includes('second') ? '30seconds' : 'minute'}${isExtendedDatapointRequired ? '&extendedDatapoint=true' : ''}`;
         return axios.get(url, { headers: header }).then((res) => res.data);
      },
      onError: (err) => toast.error(err),
      retry: false,
      refetchOnWindowFocus: false,
   });

   // --------------------------
   // ## React useEffect hooks
   // --------------------------

   useEffect(() => {
      function createLineChartData(chartData, interval) {
         const xStart = moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).valueOf();
         const xEnd = moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).valueOf();
         const alertEndMoment = moment.utc(selectedAlert.last);
         const minuteStartOfAlertEndUnixSec = alertEndMoment.clone().startOf('minute').subtract(1, 'minute').unix();
         const connectionIds = selectedAlert.connectionsDetails.map((connectionObj) => connectionObj.connectionId);

         let alertLastConnectionId = '';
         let isStreamFinishedAfterAlert = false;
         rtmpStats.data.forEach((dp, idx) => {
            if (dp.timestamp > minuteStartOfAlertEndUnixSec) {
               if (!alertLastConnectionId) {
                  alertLastConnectionId = dp.connectionId;
               } else {
                  if (dp.connectionId !== alertLastConnectionId) {
                     if (chartData.length > idx) {
                        chartData[idx] = { x: chartData[idx].x };
                        if (!isStreamFinishedAfterAlert) {
                           isStreamFinishedAfterAlert = rtmpStats.data.length - 1 !== idx;
                        }
                     }
                  }
               }
            }
            if (dp.timestamp < minuteStartOfAlertEndUnixSec) {
               if (!connectionIds.includes(dp.connectionId)) {
                  chartData[idx] = { x: chartData[idx].x };
               }
            }
         });

         const options = getOptionsWithAnnotationsEvents(
            aggEventWithSuggestedMinChartOptions(xStart, xEnd, title, interval.stepsInMilliSec / 1000, 0.5),
            getPerformanceAnnotations(selectedAlert, isStreamFinishedAfterAlert),
         );
         const colors = [];
         // calculate individual data point color
         chartData.forEach((point) => {
            if (isStrValueBeyondTolerance(point.y, TOLERANCE_LEVEL.ERROR)) {
               colors.push('#e92b2b');
            } else if (isStrValueBeyondTolerance(point.y, TOLERANCE_LEVEL.WARNING)) {
               colors.push('#ed7d0e');
            } else {
               colors.push('#34d746');
            }
         });

         const datasetsConfig = {
            label: title,
            backgroundColor: colorToRgba(getStyle('--info'), 10),
            borderColor: getStyle('--info'),
            borderWidth: 1,
            pointRadius: 2,
            pointBackgroundColor: colors,
            data: chartData,
            fill: 'origin',
            showLine: true,
            cubicInterpolationMode: 'monotone',
         };

         return { dataset: datasetsConfig, options };
      }
      if (rtmpStats?.data?.length > 0) {
         const { dataset, options } = createLineChartData(
            rtmpStats.data.map((item) => ({ x: item.timestamp * 1000, y: item.streamTimeRatio })),
            interval,
         );
         setChartData({ labels: [], datasets: [dataset] });
         setChartOptions(options);
      } else {
         setChartData({ labels: [], datasets: [] });
      }
   }, [selectedAlert, title, rtmpStats, interval, alertFineStart, alertFineEnd]);

   useEffect(() => {
      const newOccurences = STR_DETAILS.map((strType) => {
         return { name: strType.name, count: 0 };
      });
      if (chartData.datasets.length > 0) {
         chartData.datasets[0].data.forEach((datapoint) => {
            if (isStrValueBeyondTolerance(datapoint.y, TOLERANCE_LEVEL.ERROR)) {
               newOccurences.find((strType) => strType.name.toLowerCase().includes('poor')).count++;
            } else if (isStrValueBeyondTolerance(datapoint.y, TOLERANCE_LEVEL.WARNING)) {
               newOccurences.find((strType) => strType.name.toLowerCase().includes('sub')).count++;
            }
         });
      }
      setOccurences(newOccurences);
   }, [chartData]);

   // --------------------------
   // ## Constants / Variables / Functions
   // --------------------------

   // prettier-ignore
   const CSV_EXPORT_STR_URL = `/v2/troubleshooting/rtmp/${metricPath}/raw/timeseries?streams=${streamName}&from=${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}&to=${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format('YYYY-MM-DDTHH:mm:ss')}`;
   // prettier-ignore
   const FILE_NAME = `${moment.utc().format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_alert_str_id-${selectedAlert.id}_from-${moment.utc(alertFineStart, CHRONOLOGICAL_DATE_TIME_FORMAT).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}_to-${moment.utc(alertFineEnd, CHRONOLOGICAL_DATE_TIME_FORMAT).format(CHRONOLOGICAL_DATE_TIME_FORMAT_DASHED)}`;

   const dataStillInTimeRange = moment
      .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .isSameOrAfter(moment.utc().subtract(2, 'weeks'));

   const sliderRangeInMS = alertFineEndMoment.diff(alertFineStartMoment, 'milliseconds');
   const sliderRangeIsValid = sliderRangeInMS <= maxRtmpStatDisplayRangeInMS;

   const adjustedFineStart = moment
      .utc(selectedAlert.first, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .subtract(ALERT_FINE_START_REDUCTION_IN_MINUTES, 'minutes')
      .format(CHRONOLOGICAL_DATE_TIME_FORMAT);

   const adjustedFineEnd = moment
      .utc(selectedAlert.last, CHRONOLOGICAL_DATE_TIME_FORMAT)
      .add(ALERT_FINE_END_EXTENSION_IN_MINUTES, 'minutes')
      .format(CHRONOLOGICAL_DATE_TIME_FORMAT);

   return (
      <>
         {alertFineStart !== '' && alertFineEnd !== '' && (
            <BottomMargin pxSize={15}>
               <DateTimeSlider
                  startDateString={getIntervalStart(adjustedFineStart, interval.value)}
                  endDateString={getIntervalEnd(adjustedFineEnd, interval.value)}
                  fineStartString={alertFineStart}
                  fineEndString={alertFineEnd}
                  setFineInterval={setAlertFineInterval}
                  interval={interval}
                  searchType={SEARCH_TYPE.ALERT}
                  maxTimespanInMS={maxRtmpStatDisplayRangeInMS}
               />
               <BottomMargin pxSize={15} />
               <FlexRow>
                  <CenterContentContainer>
                     <HintInfo>
                        <FontWeightedText fontWeight={600}>
                           Note, that the displayed time range additionally includes:
                        </FontWeightedText>
                        <ul>
                           <li>
                              <span
                                 style={{ fontWeight: 600 }}
                              >{`${ALERT_FINE_START_REDUCTION_IN_MINUTES} minutes before`}</span>{' '}
                              the initial alert detection.
                           </li>
                           <li>
                              up to <span style={{ fontWeight: 600 }}>5 minutes after</span> the alert was resolved.
                           </li>
                        </ul>
                     </HintInfo>
                  </CenterContentContainer>
               </FlexRow>
            </BottomMargin>
         )}
         <PaddingContainer>
            <WidgetContainer $margin={'0px 0px 0px 0px'}>
               <WidgetHeaderContainer>
                  <HeightSetContainer $heightInPx={23}>
                     <TwoSideFlexContainer>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <CsvExportFromApiButton
                                 url={CSV_EXPORT_STR_URL}
                                 header={header}
                                 cancelToken={cancelToken}
                                 filename={FILE_NAME}
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                        <SimpleFlexRow>
                           <SimpleFlexItem>
                              <AlignedIconContainer
                                 data-tooltip-id='STR'
                                 data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{alertInfo}</div>)}
                                 className='bi bi-info-circle ml-1'
                              ></AlignedIconContainer>
                              <ReactTooltip
                                 id='STR'
                                 place={'top'}
                                 className={'reduced-line-height bring-on-top'}
                                 backgroundColor={'rgba(0,0,0,0.9)'}
                                 textColor={'rgb(240,240,240)'}
                                 style={{ textAlign: 'center' }}
                                 clickable
                              />
                           </SimpleFlexItem>
                        </SimpleFlexRow>
                     </TwoSideFlexContainer>
                  </HeightSetContainer>
               </WidgetHeaderContainer>
               <WidgetBodyContainer>
                  <Row>
                     <Col>
                        <CardTitle className='mb-0'>{title}</CardTitle>
                        <div className='small text-muted'>{`From ${alertFineStart} to ${alertFineEnd} \u00A0\u00A0( UTC time )`}</div>
                     </Col>
                  </Row>
                  <BottomMargin pxSize={20}>
                     {!isFetching && chartData.datasets.length > 0 && sliderRangeIsValid && (
                        <StandardContainer $paddingPx={15}>
                           <TileContainer gapPx={20} isFullScreen={mode !== E2E}>
                              {STR_DETAILS.map(({ name, description }) => (
                                 <div key={name}>
                                    <DetailTile
                                       key={name}
                                       heightPx={60}
                                       gapPx={10}
                                       style={{ cursor: 'pointer' }}
                                       data-tooltip-id={name}
                                       data-tooltip-html={ReactDOMServer.renderToStaticMarkup(<div>{description}</div>)}
                                    >
                                       <Description marginLeftPx={0} style={{ cursor: 'pointer' }}>
                                          {name}
                                       </Description>
                                       <ValueText>
                                          <TextWithToolTip
                                             text={occurences.find((strType) => strType.name === name).count}
                                             idName={name}
                                          />
                                       </ValueText>
                                    </DetailTile>
                                    {description && (
                                       <ReactTooltip
                                          id={name}
                                          place={'top'}
                                          className={'reduced-line-height bring-on-top'}
                                          backgroundColor={'rgba(0,0,0,0.9)'}
                                          textColor={'rgb(240,240,240)'}
                                          style={{ textAlign: 'center', maxWidth: '400px' }}
                                          clickable
                                       />
                                    )}
                                 </div>
                              ))}
                           </TileContainer>
                        </StandardContainer>
                     )}
                     <ChartWrapper className='chart-wrapper' height={height} marginTopPx={10}>
                        <Spinner loading={(isLoading || isFetching) && isQueryEnabled} parentTopMarginPx={60}>
                           {!sliderRangeIsValid ? (
                              <CenteredMsgShowing height={height} marginTopPx={20}>
                                 {'Selected time range is too big!'}
                                 <>
                                    <br />
                                    {`Please reduce the selected range to ${
                                       maxRtmpStatDisplayRangeInMS / 1000 / 60 / 60
                                    } hours!`}
                                 </>
                              </CenteredMsgShowing>
                           ) : (
                              <>
                                 {chartData.datasets?.length === 0 ? (
                                    <CenteredMsgShowing height={height} marginTopPx={20}>
                                       <>
                                          {'No data available'}
                                          {!dataStillInTimeRange && (
                                             <>
                                                <br />
                                                {'Details are only available for 14 days!'}
                                             </>
                                          )}
                                       </>
                                    </CenteredMsgShowing>
                                 ) : (
                                    <SetChartHeight height={height}>
                                       <Line data={chartData} options={chartOptions}></Line>
                                    </SetChartHeight>
                                 )}
                              </>
                           )}
                        </Spinner>
                     </ChartWrapper>
                  </BottomMargin>
                  <AlertDetailsPanel selectedAlert={selectedAlert} />
               </WidgetBodyContainer>
            </WidgetContainer>
         </PaddingContainer>
      </>
   );
}

StrPerformanceWidget.propTypes = {
   selectedAlert: PropTypes.object,
   streamName: PropTypes.string,
   height: PropTypes.number,
};

StrPerformanceWidget.defaultProps = {
   height: 300,
};

export default StrPerformanceWidget;
